import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderPlus, UserPlus, Users, LayoutGrid, List, SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useTeamManagement } from '@/hooks/useTeamManagement';
import TeamCard from '@/components/admin/TeamCard';
import TeamFiltersBar from '@/components/admin/TeamFiltersBar';
import UnassignedMembers from '@/components/admin/UnassignedMembers';
import MemberDetail from '@/components/admin/MemberDetail';
import CreateSalespersonModal from '@/components/team/CreateSalespersonModal';
import DeleteSalespersonModal from '@/components/team/DeleteSalespersonModal';
import CreateEditTeamModal from '@/components/team/CreateEditTeamModal';
import DeleteTeamModal from '@/components/team/DeleteTeamModal';
import type { User } from '@/types/auth';
import { cn } from '@/lib/utils';
import {
  DEFAULT_TEAM_FILTERS,
  type TeamFilters,
  applyFilters,
  enrichMembers,
  isFiltersActive,
  sortCardsMembers,
  sortTableMembers,
} from '@/utils/teamFilters';

type ViewMode = 'cards' | 'table';

const VIEW_KEY = 'team-view-mode';
const FILTERS_KEY = 'team-filters';

function getInitialView(): ViewMode {
  const stored = localStorage.getItem(VIEW_KEY);
  return stored === 'table' ? 'table' : 'cards';
}

function getInitialFilters(): TeamFilters {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return DEFAULT_TEAM_FILTERS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_TEAM_FILTERS, ...parsed };
  } catch {
    return DEFAULT_TEAM_FILTERS;
  }
}

export default function TeamManagementPage() {
  const { t } = useTranslation();
  const { teams, salespeople, unassigned, statsByUserId, loading, error, refresh, addMember, transferMember, transferAllMembers } = useTeamManagement();

  const [viewMode, setViewMode] = useState<ViewMode>(getInitialView);
  const [filters, setFilters] = useState<TeamFilters>(getInitialFilters);

  // Member detail drill-down
  const [selectedMember, setSelectedMember] = useState<{ user: User; stats?: import('@/types/session').SalespersonStats } | null>(null);

  // Modal state
  const [showCreateSP, setShowCreateSP] = useState(false);
  const [createSPTeamId, setCreateSPTeamId] = useState<string | undefined>();
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<{ id: string; name: string } | null>(null);
  const [deletingTeam, setDeletingTeam] = useState<{ id: string; name: string } | null>(null);

  // Persist filters
  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  // If the saved team filter points to a deleted team, reset to 'all'
  useEffect(() => {
    if (filters.team !== 'all' && filters.team !== 'unassigned') {
      const exists = teams.some((t2) => t2.id === filters.team);
      if (!exists && !loading && teams.length > 0) {
        setFilters((f) => ({ ...f, team: 'all' }));
      }
    }
  }, [teams, loading, filters.team]);

  const toggleView = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_KEY, mode);
  };

  const clearFilters = () => setFilters(DEFAULT_TEAM_FILTERS);

  const active = isFiltersActive(filters);

  // Sort function per view
  const sortList = (list: ReturnType<typeof enrichMembers>) =>
    viewMode === 'cards'
      ? sortCardsMembers(list, filters.sortCards)
      : sortTableMembers(list, filters.sortTable);

  // Which teams to render based on team filter
  const teamsToRender = useMemo(() => {
    if (filters.team === 'all') return teams;
    if (filters.team === 'unassigned') return [];
    return teams.filter((t2) => t2.id === filters.team);
  }, [teams, filters.team]);

  const showUnassigned = filters.team === 'all' || filters.team === 'unassigned';

  // Compute filtered+sorted per team
  const membersByTeamId = useMemo(() => {
    const map: Record<string, ReturnType<typeof enrichMembers>> = {};
    for (const team of teamsToRender) {
      const enriched = enrichMembers(team.members, statsByUserId);
      const filtered = applyFilters(enriched, filters);
      map[team.id] = sortList(filtered);
    }
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsToRender, statsByUserId, filters, viewMode]);

  const filteredUnassigned = useMemo(() => {
    if (!showUnassigned) return [];
    const enriched = enrichMembers(unassigned, statsByUserId);
    const filtered = applyFilters(enriched, filters);
    return sortList(filtered).map((m) => m.user);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassigned, statsByUserId, filters, viewMode, showUnassigned]);

  // Counters
  const totalCount = salespeople.length;
  const visibleCount =
    Object.values(membersByTeamId).reduce((n, list) => n + list.length, 0) +
    filteredUnassigned.length;

  const openCreateSP = (teamId?: string) => {
    setCreateSPTeamId(teamId);
    setShowCreateSP(true);
  };

  const handleSelectMember = (userId: string) => {
    const user = salespeople.find((sp) => sp.id === userId);
    if (!user) return;
    setSelectedMember({ user, stats: statsByUserId[userId] });
  };

  const handleSaved = () => {
    refresh();
  };

  // Member detail view (drill-down)
  if (selectedMember) {
    return (
      <div className="animate-fade-in">
        <MemberDetail
          member={selectedMember.user}
          memberStats={selectedMember.stats}
          onBack={() => setSelectedMember(null)}
        />
      </div>
    );
  }

  const showGlobalEmpty =
    !loading &&
    active &&
    visibleCount === 0 &&
    (teams.length > 0 || unassigned.length > 0);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {t('nav.admin.teams', 'Equipos')}
              </h1>
              {/* View toggle — fixed position next to title */}
              <div className="flex items-center rounded-lg border border-border p-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => toggleView('cards')}
                      className={cn(
                        'rounded-md p-1.5 transition-colors',
                        viewMode === 'cards'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                      aria-label={t('teamManagement.viewCards', 'Vista cards')}
                    >
                      <LayoutGrid className="size-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('teamManagement.viewCards', 'Vista cards')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => toggleView('table')}
                      className={cn(
                        'rounded-md p-1.5 transition-colors',
                        viewMode === 'table'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                      aria-label={t('teamManagement.viewTable', 'Vista tabla')}
                    >
                      <List className="size-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('teamManagement.viewTable', 'Vista tabla')}</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {viewMode === 'table' && (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => { setEditingTeam(null); setShowTeamModal(true); }}>
                  <FolderPlus className="size-4" />
                  {t('teamManagement.createTeam')}
                </Button>
                <Button onClick={() => openCreateSP()}>
                  <UserPlus className="size-4" />
                  {t('teamManagement.addSalesperson')}
                </Button>
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {viewMode === 'cards'
              ? t('teamManagement.subtitleCards', 'Vista general de equipos y rendimiento')
              : t('teamManagement.subtitleTable', 'Gestión de equipos y miembros')}
          </p>
        </div>

        {/* Filters */}
        {!loading && (teams.length > 0 || unassigned.length > 0) && (
          <TeamFiltersBar
            filters={filters}
            onChange={setFilters}
            onClear={clearFilters}
            teams={teams.map((team) => ({ id: team.id, name: team.name }))}
            viewMode={viewMode}
            totalCount={totalCount}
            visibleCount={visibleCount}
          />
        )}

        {/* Error */}
        {error && (
          <div role="alert" className="mb-5 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {/* Content */}
        {!loading && (
          <div className="space-y-6">
            {showGlobalEmpty ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/30 py-16">
                <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
                  <SearchX className="size-8 text-primary" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-foreground">
                  {t('teamManagement.filters.noResultsTitle', 'Sin coincidencias')}
                </h3>
                <p className="mt-2 max-w-md text-center text-sm text-muted-foreground">
                  {t('teamManagement.filters.noResultsDesc', 'Ningún miembro cumple los filtros actuales. Ajusta los filtros o límpialos para ver todos.')}
                </p>
                <div className="mt-6">
                  <Button variant="outline" onClick={clearFilters}>
                    {t('teamManagement.filters.clearFilters', 'Limpiar filtros')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {teamsToRender.map((team) => (
                  <TeamCard
                    key={team.id}
                    team={team}
                    otherTeams={teams.filter((t2) => t2.id !== team.id)}
                    unassigned={unassigned}
                    members={membersByTeamId[team.id] ?? []}
                    filtersActive={active}
                    viewMode={viewMode}
                    onSelectMember={handleSelectMember}
                    onEdit={() => { setEditingTeam({ id: team.id, name: team.name }); setShowTeamModal(true); }}
                    onDelete={() => setDeletingTeam({ id: team.id, name: team.name })}
                    onAddMember={(userId) => addMember(team.id, userId)}
                    onTransferMember={(userId, targetId) => transferMember(team.id, userId, targetId)}
                    onDeleteSalesperson={(user) => setDeletingUser(user)}
                  />
                ))}

                {showUnassigned && (
                  <UnassignedMembers
                    salespeople={filteredUnassigned}
                    teams={teams}
                    onAssign={(userId, teamId) => addMember(teamId, userId)}
                    onDelete={(user) => setDeletingUser(user)}
                  />
                )}

                {teams.length === 0 && unassigned.length === 0 && (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/30 py-16">
                    <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
                      <Users className="size-8 text-primary" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-foreground">
                      {t('teamManagement.emptyTitle')}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {t('teamManagement.emptyDesc')}
                    </p>
                    <div className="mt-6 flex gap-3">
                      <Button variant="outline" onClick={() => { setEditingTeam(null); setShowTeamModal(true); }}>
                        <FolderPlus className="size-4" />
                        {t('teamManagement.createTeam')}
                      </Button>
                      <Button onClick={() => openCreateSP()}>
                        <UserPlus className="size-4" />
                        {t('teamManagement.addSalesperson')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Modals */}
        <CreateSalespersonModal
          isOpen={showCreateSP}
          teams={teams}
          defaultTeamId={createSPTeamId}
          onClose={() => setShowCreateSP(false)}
          onCreated={handleSaved}
        />
        <DeleteSalespersonModal
          isOpen={!!deletingUser}
          salesperson={deletingUser}
          onClose={() => setDeletingUser(null)}
          onDeleted={handleSaved}
        />
        <CreateEditTeamModal
          isOpen={showTeamModal}
          teamId={editingTeam?.id}
          currentName={editingTeam?.name}
          onClose={() => { setShowTeamModal(false); setEditingTeam(null); }}
          onSaved={handleSaved}
        />
        <DeleteTeamModal
          isOpen={!!deletingTeam}
          team={deletingTeam}
          memberCount={deletingTeam ? (teams.find((t2) => t2.id === deletingTeam.id)?.members.length ?? 0) : 0}
          otherTeams={deletingTeam ? teams.filter((t2) => t2.id !== deletingTeam.id) : []}
          onTransferMembers={transferAllMembers}
          onClose={() => setDeletingTeam(null)}
          onDeleted={handleSaved}
        />
      </div>
    </TooltipProvider>
  );
}
